import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import logo from "../assets/logo.png";
import Button from '@mui/material/Button';
import ReorderIcon from '@mui/icons-material/Reorder';
import CloseIcon from '@mui/icons-material/Close';
import LanguageIcon from '@mui/icons-material/Language';
import "../styles/Header.css";
import { useTranslation } from "react-i18next";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [desktopServicesOpen, setDesktopServicesOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [showHeader, setShowHeader] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const closeMobileMenu = () => {
    setIsClosing(true);
    setTimeout(() => {
      setMobileMenuOpen(false);
      setIsClosing(false);
      setMobileServicesOpen(false);
    }, 300);
  };

  const toggleMobileMenu = () => {
    if (mobileMenuOpen) {
      closeMobileMenu();
    } else {
      setMobileMenuOpen(true);
    }
  };

  const changeLanguage = (language) => {
    i18n.changeLanguage(language);
    if (mobileMenuOpen) {
      closeMobileMenu();
    } else {
      setLanguageMenuOpen(false);
    }
  };

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setShowHeader(currentScrollY < lastScrollY || currentScrollY < 10);
          setLastScrollY(currentScrollY);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    setAnimate(true);
    setMobileMenuOpen(false);
    setMobileServicesOpen(false);
    setLanguageMenuOpen(false);
    const timeout = setTimeout(() => setAnimate(false), 800);
    return () => clearTimeout(timeout);
  }, [location.pathname]);

  return (
    <div className={`Header ${animate ? 'fade-in' : ''} ${showHeader ? 'visible' : 'hidden'}`}>
      <div className='leftSide'>
        <Link to="/" className="logoContainer">
          <img src={logo} alt="Anchmar Marine Logo" />
          <div className="logoText">
            <span>ANCHMAR</span>
            <span>MARINE AND CONSULTING</span>
          </div>
        </Link>

        <div className={`hiddenLinks ${mobileMenuOpen ? "open" : ""} ${isClosing ? "closing" : ""}`}>
          <button className="mobileCloseBtn" type="button" onClick={closeMobileMenu} aria-label="Menüyü kapat">
            <CloseIcon />
          </button>

          <Link to="/" onClick={closeMobileMenu}>
            {t('nav.home')}
          </Link>

          <Link to="/hakkimizda" onClick={closeMobileMenu}>
            {t('nav.about')}
          </Link>

          <div className="mobileServices">
            <button type="button" onClick={() => setMobileServicesOpen(prev => !prev)}>
              {t('nav.services')}
            </button>

            {mobileServicesOpen && (
              <div className="mobileDropdown">
                <Link to="/hizmetlerimiz#service-0" onClick={closeMobileMenu}>
                  {t('nav.survey')}
                </Link>
                <Link to="/hizmetlerimiz#service-1" onClick={closeMobileMenu}>
                  {t('nav.maintenance')}
                </Link>
                <Link to="/hizmetlerimiz#service-2" onClick={closeMobileMenu}>
                  {t('nav.repair')}
                </Link>
                <Link to="/hizmetlerimiz#service-3" onClick={closeMobileMenu}>
                  {t('nav.parts')}
                </Link>
              </div>
            )}
          </div>

          <Link to="/fiyatlar" onClick={closeMobileMenu}>
            {t('nav.prices', 'Fiyatlar')}
          </Link>

          <Link to="/documents" onClick={closeMobileMenu}>
            {t('nav.documents')}
          </Link>

          <Link to="/iletisim" onClick={closeMobileMenu}>
            {t('nav.contact')}
          </Link>

          <div className="languageButtons">
            <button
              type="button"
              className={i18n.language === 'tr' ? 'active' : ''}
              onClick={() => changeLanguage('tr')}
            >
              TR
            </button>
            <button
              type="button"
              className={i18n.language === 'en' ? 'active' : ''}
              onClick={() => changeLanguage('en')}
            >
              EN
            </button>
          </div>
        </div>
      </div>

      <div className='rightSide'>
        <Link to="/">{t('nav.home')}</Link>
        <Link to="/hakkimizda">{t('nav.about')}</Link>

        <div
          className="servicesMenu"
          onMouseEnter={() => setDesktopServicesOpen(true)}
          onMouseLeave={() => setDesktopServicesOpen(false)}
        >
          <span className="menuTitle">{t('nav.services')}</span>
          <div className={`dropdown ${desktopServicesOpen ? "show" : ""}`}>
            <Link to="/hizmetlerimiz#service-0">{t('nav.survey')}</Link>
            <Link to="/hizmetlerimiz#service-1">{t('nav.maintenance')}</Link>
            <Link to="/hizmetlerimiz#service-2">{t('nav.repair')}</Link>
            <Link to="/hizmetlerimiz#service-3">{t('nav.parts')}</Link>
          </div>
        </div>

        <Link to="/fiyatlar">{t('nav.prices', 'Fiyatlar')}</Link>
        <Link to="/documents">{t('nav.documents')}</Link>
        <Link to="/iletisim">{t('nav.contact')}</Link>

        <Button className="hamburgerBtn" onClick={toggleMobileMenu} aria-label="Menüyü aç">
          <ReorderIcon />
        </Button>
      </div>

      <div className="languageMenu">
        <Button onClick={() => setLanguageMenuOpen(!languageMenuOpen)} aria-label="Dil seçimi">
          <LanguageIcon />
        </Button>
        {languageMenuOpen && (
          <div className="languageDropdown">
            <button
              className={i18n.language === "tr" ? "active" : ""}
              onClick={() => changeLanguage("tr")}
            >
              TR
            </button>
            <button
              className={i18n.language === "en" ? "active" : ""}
              onClick={() => changeLanguage("en")}
            >
              EN
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Header;
